import { Box, Flex, Image, Skeleton, Text, useColorMode } from '@chakra-ui/react';
import { useKeycloak } from '@react-keycloak/web';
import React, { useContext, useEffect, useState } from 'react';
import { BrowserView } from 'react-device-detect';
import { useNavigate, useParams } from 'react-router-dom';
import DarkHeader from '../assets/headers/profile-dark.png';
import LightHeader from '../assets/headers/profile-light.png';
import { isAuthenticated } from '../auth/keycloak';
import { BrowserSidebar } from '../component/globalComponents/BrowserSidebar';
import { BrowserTopBar } from '../component/globalComponents/BrowserTopBar';
import { MobileViewPage } from '../component/globalComponents/MobileViewPage';
import { BackIcon } from '../component/iconComponents/BackIcon';
import { ProfileActions } from '../component/profileComponents/ProfileActions';
import { ProfileSocialLinks } from '../component/profileComponents/ProfileSocialLinks';
import { ProfileTabs } from '../component/profileComponents/ProfileTabs';
import { PublicProfileInfo } from '../component/profileComponents/PublicProfileInfo';
import config from '../config';
import PageDataContext from '../context/PageDataContext';
import { usePageDataCache } from '../hooks/usePageDataCache';
import { getGameName } from '../service/lobby';
import { getUserPublicProfile, isLive, UserProfile } from '../service/social';

interface ProfileProps {
    children?: React.ReactNode;
}

export const PublicProfilePage: React.FC<ProfileProps> = () => {
    const { userID } = useParams();
    const { keycloak } = useKeycloak();
    const { colorMode } = useColorMode();
    const { isLoadFromCache } = useContext(PageDataContext);

    const navigate = useNavigate();

    const [userProfile, setUserProfile] = usePageDataCache<UserProfile | undefined>(
        'userProfile',
        undefined,
    );

    const [imageUrl, setImageUrl] = usePageDataCache(
        'imageUrl',
        userProfile?.avatar_object_url || '',
    );
    const [live, setLive] = usePageDataCache('live', false);
    const [isLoading, setIsLoading] = useState(true);

    const [activeGameName, setActiveGameName] = usePageDataCache('activeGameName', '');

    useEffect(() => {
        async function loadProfileInfo() {
            if (!userID) {
                return;
            }
            // We want to skip the cache if the user is looking at themselves as
            // they are likely checking updates on their own profile
            const skipCache = userID === keycloak.tokenParsed?.sub;

            const profileResult = await getUserPublicProfile(userID, skipCache);

            const { isUserLive, gameInfo } = await isLive(profileResult);

            const activeGameLicense = getGameName(gameInfo?.license);
            setActiveGameName(activeGameLicense);

            setLive(isUserLive);
            setUserProfile(profileResult);
        }
        if (!isLoadFromCache) {
            loadProfileInfo();
        }
    }, []);

    useEffect(() => {
        if (userProfile) {
            let avatarUrl = config.avatarPlaceholderURL;
            if (userProfile.avatar_object_url) {
                avatarUrl = userProfile.avatar_object_url;
            } else if (userProfile.twitch_profile) {
                avatarUrl = userProfile.twitch_profile?.profile_image_url;
            }

            setImageUrl(avatarUrl);
            setIsLoading(false);
        }
    }, [userProfile]);

    if (!userID) {
        return <></>;
    }

    return (
        <>
            {!isLoading && (
                <>
                    <BrowserView>
                        <BrowserTopBar color="#FFF" isLoading={isLoading} />
                        <BrowserSidebar />
                        <Box className="browser-content">
                            <Flex className="profile-expand-container">
                                <Flex>
                                    <Image
                                        className="profile-banner"
                                        src={colorMode === 'dark' ? DarkHeader : LightHeader}
                                        fallback={<Skeleton height="100%" width="100%" />}
                                    />
                                </Flex>
                                {userProfile && (
                                    <PublicProfileInfo
                                        userProfile={userProfile}
                                        keycloak={keycloak}
                                        imageUrl={imageUrl}
                                        isLive={live}
                                    />
                                )}
                                <Flex className="profile-divider">
                                    <Flex className={'divider'} />
                                </Flex>
                                <ProfileTabs userProfile={userProfile} />
                            </Flex>
                        </Box>
                    </BrowserView>
                    <MobileViewPage
                        title=""
                        titleColor=""
                        titleBackgroundColor="#6ef1d5"
                        customBanner={
                            <Flex className="profile-back-button" onClick={() => navigate(-1)}>
                                <BackIcon />
                            </Flex>
                        }
                        isLoading={isLoading}
                    >
                        <Flex className="profile-expand-container">
                            <Image
                                className="profile-banner"
                                src={colorMode === 'dark' ? DarkHeader : LightHeader}
                                fallback={<Skeleton height="100%" width="100%" />}
                            />
                            <Flex className={'public-profile-info'}>
                                {userProfile && (
                                    <PublicProfileInfo
                                        userProfile={userProfile}
                                        keycloak={keycloak}
                                        imageUrl={imageUrl}
                                        isLive={live}
                                    />
                                )}

                                <Flex className={'divider'} />

                                {userProfile && (
                                    <Flex className={'profile-live-links'}>
                                        <Text>Watch Now!</Text>
                                        <ProfileSocialLinks profile={userProfile} />
                                    </Flex>
                                )}

                                <Flex className={'divider'} />

                                {isAuthenticated(keycloak) &&
                                    userProfile?.id !== keycloak.tokenParsed?.sub && (
                                        <ProfileActions
                                            userID={userID}
                                            keycloak={keycloak}
                                            userProfile={userProfile}
                                        />
                                    )}

                                <Flex className={'divider'} />
                            </Flex>
                            <ProfileTabs userProfile={userProfile} />
                        </Flex>
                    </MobileViewPage>
                </>
            )}
        </>
    );
};
